/**
 * Who may sign in to the tenant, and who may read its approved programmes.
 *
 * Two either-or decisions with real consequences, so they are cards a reader
 * can compare rather than closed selects that show one answer at a time.
 *
 * @module components/admin/AccessPolicySettings
 */

import React from "react";

import { Button, ErrorAlert, MutedText, RadioCardGroup, Text } from "@/components/ui";
import { Bone } from "@/components/ui/SkeletonParts";
import type { TenantAccessPolicy } from "@/server/types";

type OnboardingMode = TenantAccessPolicy["onboardingMode"];
type ProgrammeAccess = TenantAccessPolicy["approvedProgrammeAccess"];

export interface AccessPolicySettingsProps {
  /** Null until the read answers, and if it fails. */
  policy: TenantAccessPolicy | null;
  loading: boolean;
  /** True while a save is in flight, so the button refuses a second press. */
  saving: boolean;
  onChange: (policy: TenantAccessPolicy) => void;
  feedback: string;
  /** Whether the feedback reports a failure. */
  failed?: boolean;
  onSave: () => void;
  /** Read the policy again, offered when the first read failed. */
  onRetry?: () => void;
}

/**
 * @example
 * ```tsx
 * <AccessPolicySettings policy={policy} onChange={setPolicy} feedback={feedback} onSave={save} />
 * ```
 */
export function AccessPolicySettings({
  policy,
  loading,
  saving,
  onChange,
  feedback,
  failed = false,
  onSave,
  onRetry = undefined,
}: AccessPolicySettingsProps): React.ReactElement {
  if (loading) {
    return (
      <div role="status" aria-live="polite" className="grid gap-6">
        <span className="sr-only">Loading the access policy</span>
        {["Who can join", "Who can read approved programmes"].map((legend) => (
          <div key={legend} aria-hidden="true" className="flex flex-col gap-2">
            <Text size="sm" className="font-semibold">
              {legend}
            </Text>
            <div className="grid gap-3 sm:grid-cols-2">
              <Bone className="h-16 w-full" />
              <Bone className="h-16 w-full" />
            </div>
          </div>
        ))}
        <Bone className="h-9 w-40" />
      </div>
    );
  }
  if (!policy) {
    // Nothing to edit and nothing to save: showing the cards here would put a
    // policy on screen that no one chose.
    return (
      <ErrorAlert>
        <span>{feedback || "Unable to load the access policy"}</span>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </ErrorAlert>
    );
  }
  // A save's answer replaces the policy, so a choice made meanwhile would be lost.
  const hold = saving
    ? { disabled: true, disabledReason: "Wait for the save in progress to finish." }
    : {};
  return (
    <div className="grid gap-6">
      <RadioCardGroup<OnboardingMode>
        legend="Who can join"
        showLegend
        value={policy.onboardingMode}
        onChange={(onboardingMode) => {
          onChange({ ...policy, onboardingMode });
        }}
        options={[
          {
            value: "invite-only",
            label: "Invite only",
            description: "Only people you invite can reach the tenant.",
            icon: "envelope-simple",
            ...hold,
          },
          {
            value: "organisation-domain",
            label: "Verified organisation domain",
            description: "Anyone with a verified organisation address can onboard themselves.",
            icon: "buildings",
            ...hold,
          },
        ]}
        data-testid="tenant-onboarding-mode"
      />
      <RadioCardGroup<ProgrammeAccess>
        legend="Who can read approved programmes"
        showLegend
        value={policy.approvedProgrammeAccess}
        onChange={(approvedProgrammeAccess) => {
          onChange({ ...policy, approvedProgrammeAccess });
        }}
        options={[
          {
            value: "all-members",
            label: "All tenant members",
            description: "Everyone in the tenant can read an approved programme.",
            icon: "users-three",
            ...hold,
          },
          {
            value: "assigned-only",
            label: "Assigned users only",
            description: "Only the people assigned to a programme can read it.",
            icon: "lock-key",
            ...hold,
          },
        ]}
        data-testid="approved-programme-access"
      />
      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Saving the access policy"
          onClick={onSave}
        >
          Save access policy
        </Button>
        {/* Announced: the save changes nothing else on the screen, so the
            line is the only thing that says whether it took. */}
        <span role="status" aria-live="polite">
          {feedback && !failed ? <MutedText as="span">{feedback}</MutedText> : null}
        </span>
      </div>
      {feedback && failed ? <ErrorAlert>{feedback}</ErrorAlert> : null}
    </div>
  );
}
